/**
 * RunMapScreen — the between-rounds progression view.
 *
 * Design notes:
 *   - Only ONE act is rendered at a time. The act is derived from the
 *     player's current position: act 1 until they complete a row-6 node,
 *     then act 2 unlocks with fresh row-7 entries (act 2 tree is not
 *     connected to act 1). This makes act transitions feel like a step
 *     into new territory.
 *   - Nodes wiggle subtly on a sin/cos loop; edges follow them live.
 *   - Hovering a node glows every downstream line + node.
 *   - Hover tooltip fetches a Wikipedia thumbnail for the node's city
 *     and caches the URL by city name.
 *   - On first mount the scroll container jumps to the bottom so the
 *     player's entry row is in view.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { theme } from '../../styles/theme';
import { RunState } from '../../types/run';
import { RunMapNode, RunMapNodeType, ROWS_PER_ACT } from '../../types/runMap';
import type { AgentLoadout } from '../../types/game';
import {
  getReachableNodes,
  getCurrentAct,
  getDescendantIds,
  actRows,
} from '../../engine/runMap';
import { CURATED_CITIES } from '../../data/cities';
import { SpinningGlobe } from '../ui/SpinningGlobe';

const WEAPON_LABEL: Record<string, string> = {
  'pistol': 'PISTOL',
  'sniper-rifle': 'SNIPER',
  'combat-knife': 'KNIFE',
};
const ARCHETYPE_LABEL: Record<string, string> = {
  tracker: 'TRACKER',
  commander: 'COMMANDER',
  ghost: 'GHOST',
};

interface RunMapScreenProps {
  runState: RunState;
  onNodeSelected: (nodeId: string) => void;
  onEndRun: () => void;
}

// Visual layout
const NODE_R = 22;
const NODE_SPACING_X = 110;
const NODE_SPACING_Y = 96;
const PADDING_X = 80;
const PADDING_Y = 100;

const NODE_STYLES: Record<RunMapNodeType, { label: string; icon: string; color: string; rewardText: string }> = {
  hit:        { label: 'HIT',         icon: '◎', color: '#ff5555', rewardText: 'New passive on kill' },
  elite:      { label: 'ELITE',       icon: '☠',  color: '#ff8855', rewardText: 'New passive + marks bump' },
  extraction: { label: 'EXTRACTION',  icon: '⇪', color: '#44aaff', rewardText: 'New card (3-card draft)' },
  hq:         { label: 'HQ',          icon: '▣', color: '#ffdd44', rewardText: 'Shop access (packs, direct buy, card removal)' },
  boss:       { label: 'BOSS',        icon: '♛', color: '#ff44cc', rewardText: 'Run complete' },
};

// --- Wikipedia image cache ---
// One fetch per city per session, cached in-memory. Null means "tried and failed".
const wikiImageCache = new Map<string, string | null>();

function fetchWikiImage(cityName: string, displayName: string | undefined): Promise<string | null> {
  if (wikiImageCache.has(cityName)) return Promise.resolve(wikiImageCache.get(cityName)!);
  // Prefer the displayName's first part (canonical English title); fall back to name.
  const title = (displayName?.split(',')[0]?.trim() ?? cityName).replace(/\s+/g, '_');
  const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`;
  return fetch(url)
    .then(res => res.ok ? res.json() : null)
    .then(data => {
      const src = data?.thumbnail?.source ?? data?.originalimage?.source ?? null;
      wikiImageCache.set(cityName, src);
      return src;
    })
    .catch(() => {
      wikiImageCache.set(cityName, null);
      return null;
    });
}

function cityDisplayName(cityName: string | null): string {
  if (!cityName) return '—';
  const city = CURATED_CITIES.find(c => c.name === cityName);
  return city?.displayName ?? cityName;
}

function cityShortCode(cityName: string | null): string {
  if (!cityName) return '—';
  return cityName.slice(0, 3).toUpperCase();
}

/** Stable hash for per-node wiggle phase so nodes don't all oscillate in sync. */
function stringHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

export function RunMapScreen({ runState, onNodeSelected, onEndRun }: RunMapScreenProps) {
  const map = runState.runMap;
  const completedIds = useMemo(
    () => new Set(runState.completedNodeIds ?? []),
    [runState.completedNodeIds],
  );

  // --- Which act to render ---
  const act: 1 | 2 = useMemo(() => {
    if (!map) return 1;
    return getCurrentAct(map, runState.currentNodeId ?? null, runState.completedNodeIds ?? []);
  }, [map, runState.currentNodeId, runState.completedNodeIds]);

  const visibleRows = useMemo(() => actRows(act), [act]);
  const minVisibleRow = visibleRows[0];
  const maxVisibleRow = visibleRows[visibleRows.length - 1];

  const visibleNodes = useMemo(() => {
    if (!map) return [];
    return map.nodes.filter(n => n.row >= minVisibleRow && n.row <= maxVisibleRow);
  }, [map, minVisibleRow, maxVisibleRow]);

  // --- Reachability ---
  const reachableIds = useMemo(() => {
    if (!map) return new Set<string>();
    return new Set(
      getReachableNodes(map, runState.currentNodeId ?? null, runState.completedNodeIds ?? [])
        .map(n => n.id),
    );
  }, [map, runState.currentNodeId, runState.completedNodeIds]);

  // Row widths within this act
  const rowWidths = useMemo(() => {
    const widths: number[] = [];
    for (const r of visibleRows) {
      widths.push(visibleNodes.filter(n => n.row === r).length);
    }
    return widths;
  }, [visibleRows, visibleNodes]);

  const maxRowWidth = Math.max(...rowWidths, 1);

  const svgW = PADDING_X * 2 + (maxRowWidth - 1) * NODE_SPACING_X + NODE_R * 2;
  const svgH = PADDING_Y * 2 + (ROWS_PER_ACT - 1) * NODE_SPACING_Y + NODE_R * 2;

  // --- Wiggle animation clock ---
  const [clock, setClock] = useState(0);
  useEffect(() => {
    let raf = 0;
    const tick = (t: number) => {
      setClock(t / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // --- Node screen positions (with wiggle) ---
  const nodePos = useCallback((node: RunMapNode): { x: number; y: number } => {
    const rowIndex = node.row - minVisibleRow; // 0..ROWS_PER_ACT-1
    const rowWidth = rowWidths[rowIndex];
    const rowStartX = PADDING_X + (maxRowWidth - rowWidth) * NODE_SPACING_X / 2;
    const baseX = rowStartX + node.col * NODE_SPACING_X + NODE_R;
    const baseY = svgH - PADDING_Y - rowIndex * NODE_SPACING_Y - NODE_R;

    // Per-node wiggle: small amplitude, slow frequency, phase keyed to node id
    // so adjacent nodes drift independently.
    const phase = stringHash(node.id) * 0.0001;
    const dx = Math.sin(clock * 0.6 + phase) * 3;
    const dy = Math.cos(clock * 0.5 + phase * 1.7) * 3;
    return { x: baseX + dx, y: baseY + dy };
  }, [rowWidths, maxRowWidth, svgH, minVisibleRow, clock]);

  // --- Hover state + descendants for path highlighting ---
  // `hoverAnchor` captures the hovered node's screen-space position so the
  // tooltip (rendered via position: fixed) sits next to the node regardless
  // of scroll offset or the flex-centering inside the scroll container.
  // Anchor is captured once on mouseEnter — the ~3px wiggle amplitude isn't
  // worth chasing with rAF updates.
  const [hoverNodeId, setHoverNodeId] = useState<string | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<{ x: number; y: number; r: number } | null>(null);

  const handleNodeEnter = useCallback((nodeId: string, ev: React.MouseEvent<SVGGElement>) => {
    setHoverNodeId(nodeId);
    const rect = (ev.currentTarget as SVGGElement).getBoundingClientRect();
    setHoverAnchor({
      x: rect.right,
      y: rect.top + rect.height / 2,
      r: rect.width / 2,
    });
  }, []);

  const handleNodeLeave = useCallback((nodeId: string) => {
    setHoverNodeId(prev => (prev === nodeId ? null : prev));
    setHoverAnchor(null);
  }, []);
  const descendantIds = useMemo(() => {
    if (!map || !hoverNodeId) return new Set<string>();
    return getDescendantIds(map, hoverNodeId);
  }, [map, hoverNodeId]);

  // --- Wikipedia image fetch for hovered node ---
  const [hoverImage, setHoverImage] = useState<string | null>(null);
  const hoverImageReqIdRef = useRef(0);
  useEffect(() => {
    const node = hoverNodeId && map ? map.nodes.find(n => n.id === hoverNodeId) : null;
    if (!node || !node.cityName) {
      setHoverImage(null);
      return;
    }
    const cached = wikiImageCache.get(node.cityName);
    if (cached !== undefined) {
      setHoverImage(cached);
      return;
    }
    const reqId = ++hoverImageReqIdRef.current;
    setHoverImage(null);
    fetchWikiImage(node.cityName, cityDisplayName(node.cityName)).then(url => {
      if (reqId === hoverImageReqIdRef.current) setHoverImage(url);
    });
  }, [hoverNodeId, map]);

  // --- Scroll-to-bottom on first mount so the entry row is visible ---
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    // Only on act change / mount — don't re-run every frame from wiggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act]);

  if (!map) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>No map generated for this run.</div>
      </div>
    );
  }

  const currentNodeId = runState.currentNodeId ?? null;
  const hoveredNode = hoverNodeId ? map.nodes.find(n => n.id === hoverNodeId) : null;

  const subtitle = (() => {
    if (!currentNodeId) return 'Select entry point';
    const current = map.nodes.find(n => n.id === currentNodeId);
    const isAct1EndCompleted =
      current?.row === ROWS_PER_ACT &&
      completedIds.has(currentNodeId) &&
      act === 2;
    if (isAct1EndCompleted) return 'Act 1 complete — select new entry point';
    return 'Select next destination';
  })();

  return (
    <div style={styles.container}>
      {/* Ambient background globe — same treatment as the main menu but
          dimmer (opacity 0.18 vs 0.28) so it doesn't compete with the
          map nodes for attention. Positioned absolute, sized relative
          to the viewport; pointerEvents handled inside the component. */}
      <div style={styles.globeWrapper} aria-hidden>
        <SpinningGlobe
          size={640}
          color="#44ccff"
          rotationSpeed={0.1}
          pointCount={480}
          neighbors={4}
          style={{ opacity: 0.18 }}
        />
      </div>
      <div style={styles.header}>
        {/* Left spacer keeps the title centered against the END RUN button
            on the right. Empty on purpose. */}
        <div style={styles.headerSpacer} />
        <div style={styles.headerCenter}>
          <div style={styles.titleAct}>ACT {act}</div>
          <div style={styles.titleCaption}>RUN PATH</div>
        </div>
        <div style={styles.headerRight}>
          <button
            className="run-map-button"
            style={styles.endRunButton}
            onClick={onEndRun}
          >
            END RUN
          </button>
        </div>
      </div>

      <div ref={scrollRef} style={styles.scrollWrap}>
        <svg
          className="run-map-svg"
          width={svgW}
          height={svgH}
          style={styles.svg}
          viewBox={`0 0 ${svgW} ${svgH}`}
        >
          <defs>
            <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Edges — drawn first so nodes sit on top */}
          {visibleNodes.flatMap(src =>
            src.nextNodeIds.map(tgtId => {
              const tgt = map.nodes.find(n => n.id === tgtId);
              if (!tgt || tgt.row < minVisibleRow || tgt.row > maxVisibleRow) return null;
              const a = nodePos(src);
              const b = nodePos(tgt);
              const srcCompleted = completedIds.has(src.id);
              const tgtCompleted = completedIds.has(tgt.id);
              const isTravelled = srcCompleted && tgtCompleted;
              const isLive = src.id === currentNodeId && reachableIds.has(tgt.id);
              // Downstream-of-hover: edge is on a path leading away from the
              // hovered node (its source is the hover node OR its source is
              // itself a descendant of hover).
              const isOnHoverPath =
                hoverNodeId &&
                (src.id === hoverNodeId || descendantIds.has(src.id)) &&
                descendantIds.has(tgt.id);

              let stroke: string;
              let strokeWidth: number;
              if (isOnHoverPath) {
                stroke = '#44ccff';
                strokeWidth = 2.5;
              } else if (isTravelled) {
                stroke = 'rgba(0, 255, 136, 0.5)';
                strokeWidth = 2;
              } else if (isLive) {
                stroke = 'rgba(0, 255, 136, 0.8)';
                strokeWidth = 2;
              } else {
                stroke = 'rgba(136, 153, 170, 0.25)';
                strokeWidth = 1;
              }
              return (
                <line
                  key={`${src.id}-${tgt.id}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  filter={isOnHoverPath ? 'url(#node-glow)' : undefined}
                  style={{ transition: 'stroke 180ms ease, stroke-width 180ms ease' }}
                />
              );
            }),
          )}

          {/* Nodes */}
          {visibleNodes.map(node => {
            const { x, y } = nodePos(node);
            const isCurrent = node.id === currentNodeId;
            const isCompleted = completedIds.has(node.id);
            const isReachable = reachableIds.has(node.id);
            const isClickable = isReachable;
            const isHovered = node.id === hoverNodeId;
            const isOnHoverPath = hoverNodeId && descendantIds.has(node.id);
            const style = NODE_STYLES[node.type];

            let fillColor: string;
            let strokeColor: string;
            let strokeWidth = 2;
            let opacity = 1;

            if (isCompleted) {
              fillColor = 'rgba(60, 70, 85, 0.7)';
              strokeColor = 'rgba(136, 153, 170, 0.5)';
              opacity = 0.65;
            } else if (isCurrent) {
              fillColor = style.color;
              strokeColor = '#00ff88';
              strokeWidth = 4;
            } else if (isClickable) {
              fillColor = style.color;
              strokeColor = isHovered ? '#ffffff' : 'rgba(255, 255, 255, 0.7)';
              strokeWidth = isHovered ? 3 : 2;
            } else if (isOnHoverPath) {
              fillColor = style.color;
              strokeColor = '#44ccff';
              strokeWidth = 2.5;
              opacity = 0.85;
            } else {
              fillColor = style.color;
              strokeColor = 'rgba(136, 153, 170, 0.3)';
              opacity = 0.45;
            }

            const isBoss = node.type === 'boss';
            const r = isBoss ? NODE_R + 6 : NODE_R;

            return (
              <g
                key={node.id}
                className={isClickable ? 'run-map-node run-map-node--clickable' : 'run-map-node'}
                style={{ cursor: isClickable ? 'pointer' : 'default', opacity }}
                onClick={isClickable ? () => onNodeSelected(node.id) : undefined}
                onMouseEnter={(e) => handleNodeEnter(node.id, e)}
                onMouseLeave={() => handleNodeLeave(node.id)}
              >
                {isClickable && !isCurrent && (
                  <circle
                    cx={x}
                    cy={y}
                    r={r + 6}
                    fill="none"
                    stroke={style.color}
                    strokeWidth={1}
                    opacity={0.45}
                  >
                    <animate
                      attributeName="r"
                      values={`${r + 4};${r + 10};${r + 4}`}
                      dur="1.6s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.45;0;0.45"
                      dur="1.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
                <circle
                  cx={x}
                  cy={y}
                  r={r}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  filter={isOnHoverPath || isHovered ? 'url(#node-glow)' : undefined}
                  style={{ transition: 'stroke 180ms ease, stroke-width 180ms ease' }}
                />
                <text
                  x={x}
                  y={y + 6}
                  textAnchor="middle"
                  fill={isCompleted ? 'rgba(220, 230, 240, 0.5)' : '#fff'}
                  fontFamily={theme.fonts.mono}
                  fontSize={isBoss ? 24 : 18}
                  fontWeight={700}
                  pointerEvents="none"
                >
                  {style.icon}
                </text>
                <text
                  x={x}
                  y={y + r + 14}
                  textAnchor="middle"
                  fill={isCompleted ? theme.colors.textDim : theme.colors.textSecondary}
                  fontFamily={theme.fonts.mono}
                  fontSize={10}
                  letterSpacing={1.5}
                  fontWeight={600}
                  pointerEvents="none"
                >
                  {node.type === 'hq' ? 'SHOP' : cityShortCode(node.cityName)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip — position: fixed, anchored to the node's screen
            rect captured on mouseEnter. Using fixed (not absolute-in-scroll)
            decouples the tooltip from the SVG's flex-centering and scroll
            offset, so it reliably sits next to the node on screen. */}
        {hoveredNode && hoverAnchor && (() => {
          const style = NODE_STYLES[hoveredNode.type];
          // Flip to left side if the right side would overflow.
          const TOOLTIP_W = 280;
          // Tall enough to fit the Act 1 full agent readout + city image.
          const TOOLTIP_H_ESTIMATE = 320;
          const GAP = 12;
          const leftOfNode = hoverAnchor.x - hoverAnchor.r * 2 - GAP - TOOLTIP_W;
          const rightOfNode = hoverAnchor.x + GAP;
          const fitsRight = rightOfNode + TOOLTIP_W <= window.innerWidth - 8;
          const left = fitsRight ? rightOfNode : Math.max(8, leftOfNode);
          const rawTop = hoverAnchor.y - TOOLTIP_H_ESTIMATE / 2;
          const top = Math.max(
            8,
            Math.min(rawTop, window.innerHeight - TOOLTIP_H_ESTIMATE - 8),
          );
          return (
            <div
              style={{
                ...styles.tooltip,
                left,
                top,
              }}
            >
              <div style={{ ...styles.tooltipHeader, color: style.color }}>
                {style.label}
              </div>
              {hoveredNode.type !== 'hq' && (
                <>
                  <div style={styles.tooltipRow}>
                    <span style={styles.tooltipLabel}>CITY:</span>{' '}
                    <span style={styles.tooltipValue}>{cityDisplayName(hoveredNode.cityName)}</span>
                  </div>
                  {hoverImage && (
                    <img
                      src={hoverImage}
                      alt={cityDisplayName(hoveredNode.cityName)}
                      style={styles.tooltipImage}
                      draggable={false}
                    />
                  )}
                </>
              )}
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>REWARD:</span>{' '}
                <span style={styles.tooltipValue}>{style.rewardText}</span>
              </div>
              {hoveredNode.type !== 'hq' && (() => {
                // Act 1: reveal pre-rolled agent details inline. Act 2: only
                // the generic "UNKNOWN — reveal via safehouse" line, since
                // the player has to earn that intel in-round for Act 2.
                const loadout = (hoveredNode.agentLoadout ?? null) as AgentLoadout | null;
                if (act === 1 && loadout) {
                  const weaponLabel = loadout.weaponId
                    ? WEAPON_LABEL[loadout.weaponId] ?? loadout.weaponId.toUpperCase()
                    : 'UNARMED';
                  const archetype = ARCHETYPE_LABEL[loadout.archetype] ?? loadout.archetype.toUpperCase();
                  const passiveNames = loadout.passives.map(p => p.name).join(', ');
                  return (
                    <>
                      <div style={styles.tooltipDivider} />
                      <div style={styles.tooltipRow}>
                        <span style={styles.tooltipLabel}>AGENT:</span>{' '}
                        <span style={styles.tooltipValue}>{loadout.codename}</span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <span style={styles.tooltipLabel}>WEAPON:</span>{' '}
                        <span style={styles.tooltipValue}>{weaponLabel}</span>
                      </div>
                      <div style={styles.tooltipRow}>
                        <span style={styles.tooltipLabel}>ARCHETYPE:</span>{' '}
                        <span style={styles.tooltipValue}>{archetype}</span>
                      </div>
                      {loadout.passives.length > 0 && (
                        <div style={styles.tooltipRow}>
                          <span style={styles.tooltipLabel}>PASSIVES:</span>{' '}
                          <span style={styles.tooltipValue}>{passiveNames}</span>
                        </div>
                      )}
                    </>
                  );
                }
                return (
                  <div style={styles.tooltipRow}>
                    <span style={styles.tooltipLabel}>
                      AGENT INTEL: UNKNOWN — reveal via safehouse
                    </span>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </div>

      {/* First-pick helper text — only shown when no node is selected yet
          (i.e. the player is about to choose their entry). Floats above
          the footer so it sits close to the row-1 entry nodes without
          getting lost in the scroll content. Suppressed for all later
          picks so it doesn't become visual noise. */}
      {!currentNodeId && (
        <div style={styles.helperBar}>
          <span style={styles.helperPulse}>▸</span>
          <span style={styles.helperText}>{subtitle}</span>
          <span style={styles.helperPulse}>◂</span>
        </div>
      )}

      <div style={styles.footer}>
        <div style={styles.footerLine}>
          ACT {act} / 2 — {runState.completedNodeIds?.length ?? 0} NODES CLEARED
        </div>
        <div style={styles.footerLine}>
          MARKS: {runState.marks} banked, {runState.unbankedMarks} unbanked
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: theme.colors.background,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.fonts.mono,
    color: theme.colors.textPrimary,
    overflow: 'hidden',
  },
  // Absolute, centered, behind everything. pointerEvents none so clicks
  // pass through to the map nodes / header buttons.
  globeWrapper: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    // Translucent so the globe is faintly visible through the header
    // strip as well — prevents a hard opaque band across the top.
    background: 'rgba(16, 22, 31, 0.85)',
    position: 'relative',
    zIndex: 2,
  },
  // Left-side spacer: same width as the END RUN button so the title stays
  // optically centered in the header bar.
  headerSpacer: {
    width: '100px',
  },
  headerCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100px',
  },
  // Act counter is now the page title — big, bold, centered.
  titleAct: {
    fontSize: '28px',
    fontWeight: 700,
    letterSpacing: '8px',
    color: theme.colors.textPrimary,
    lineHeight: 1,
  },
  titleCaption: {
    fontSize: '9px',
    letterSpacing: '4px',
    color: theme.colors.textDim,
    marginTop: '6px',
  },
  // Helper bar for first-pick hint, floats above the footer.
  helperBar: {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px 24px',
    background: 'rgba(68, 204, 136, 0.06)',
    borderTop: '1px solid rgba(68, 204, 136, 0.2)',
    borderBottom: '1px solid rgba(68, 204, 136, 0.2)',
  },
  helperPulse: {
    color: 'rgba(68, 204, 136, 0.8)',
    fontSize: '12px',
    animation: 'pulse 1.6s ease-in-out infinite',
  },
  helperText: {
    fontSize: '11px',
    letterSpacing: '3px',
    color: '#9fe0c0',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
  endRunButton: {
    background: 'rgba(255, 80, 80, 0.08)',
    border: '1px solid rgba(255, 80, 80, 0.5)',
    color: '#ff8888',
    padding: '8px 16px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '3px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '3px',
  },
  scrollWrap: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    padding: '20px',
    // Sit above the background globe; don't introduce an opaque fill
    // so the globe shows through behind the map edges/padding.
    zIndex: 1,
  },
  svg: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
  },
  tooltip: {
    position: 'fixed',
    background: 'rgba(8, 12, 20, 0.96)',
    border: `1px solid ${theme.colors.border}`,
    padding: '10px 14px',
    borderRadius: '4px',
    pointerEvents: 'none',
    fontSize: '11px',
    width: '280px',
    zIndex: 100,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
  },
  tooltipHeader: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '3px',
    marginBottom: '8px',
  },
  tooltipRow: {
    marginTop: '4px',
    lineHeight: 1.5,
  },
  tooltipLabel: {
    color: theme.colors.textDim,
    fontSize: '9px',
    letterSpacing: '1.5px',
    fontWeight: 600,
  },
  tooltipValue: {
    color: theme.colors.textPrimary,
    fontSize: '11px',
  },
  tooltipDivider: {
    height: '1px',
    background: theme.colors.border,
    margin: '8px 0 4px 0',
  },
  tooltipImage: {
    display: 'block',
    width: '100%',
    maxHeight: '140px',
    objectFit: 'cover',
    marginTop: '8px',
    marginBottom: '4px',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
  },
  footer: {
    padding: '12px 24px',
    borderTop: `1px solid ${theme.colors.border}`,
    background: 'rgba(16, 22, 31, 0.85)',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 2,
  },
  footerLine: {
    fontSize: '10px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
  },
  errorText: {
    padding: '40px',
    textAlign: 'center',
    color: theme.colors.textSecondary,
  },
};
